import React, { memo, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ModalId } from '../../../Components/New/Modal/ProfileModal/Const'
import { useAppDispatch, useAppSelector } from '../../../ReduxTool/store/hooks'
import { NewModal } from '../../../Components/New/Modal/NewModal'
import { setModalHeaderFooter } from '../../../ReduxTool/Slice/CommonReducers/CommonReducers'
import UserAddress from './UserAddress'
import { getUserDetails, UserDetailsAction } from '../../../ReduxTool/Slice/Auth/asyncHandlers'
import { IconSave } from '../../../assests/icons/MapToolsIcons'
import { countryJsonType } from '../../../ReduxTool/Slice/Auth/types'
import { loadCountryMaster, normalizeCountryPostalInput, postalInputMaxLength, validateCountryPostalCode } from '../../../Utils/countryMaster'

export interface UserAddressTy { country: string; state: string; city: string; pincode: string; address: string; }

export const  UserAddressModal = () => {
  const dispatch = useAppDispatch();
  const { user: { address: UserGlobalAddress }, user } = useAppSelector((state) => state.auth); 
  const { modal: { modalid } } = useAppSelector(state => state.commonReducers)


  const [userAddress, setUserAddress] = useState<UserAddressTy>({ country: "", state: "", city: "", pincode: "", address: "" });
  const [addressLoading,setAddressLoading]= useState(false)
   const [errors, setErrors] = useState<Record<string,string>>({});
  const [countries, setCountries] = useState<countryJsonType[]>([]);
  useEffect(() => {
    let mounted = true;
    loadCountryMaster().then(data => { if (mounted) setCountries(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  const accountCountry = countries.find(country => country.countryid === user.countryid ||
    country.alpha2code === user.country_mstr?.alpha2code) ?? user.country_mstr;
  const postalMaxLength = postalInputMaxLength(accountCountry);


  // --- SCENARIO LOGIC: Toggle Between Profile and Address Modals ---
  const handleModalTransition = (targetModal: "profile" | "address") => {
    if (targetModal === "address") {
      // setProfileModals((prev) => ({ ...prev, view: false, address: true }));
      dispatch(setModalHeaderFooter({ title: "Edit Address", btnTxt: "Save", secondaryBtnTxt: "Cancel", modalid: ModalId.UserAddress,isheader:true }));
    } else {
      // setProfileModals((prev) => ({ ...prev, view: true, address: false }));
      dispatch(setModalHeaderFooter({ title: "Account Information", btnTxt: "Save", secondaryBtnTxt: "",modalid: ModalId.UserProfileEditMode, isheader: true }));
    }
  };

  // --- SCENARIO LOGIC: Handle Global String to Local Object ---
  useEffect(() => {
    if (UserGlobalAddress) {
      try {
        // Strict Check 1: Attempt JSON parse just in case it was saved as JSON
        const parsedObj = JSON.parse(UserGlobalAddress);
        if (typeof parsedObj === "object" && parsedObj !== null) {
          setUserAddress({ country: parsedObj.country || user?.country_mstr?.countryname, state: parsedObj.state || "",  city: parsedObj.city || "",  pincode: parsedObj.pincode || "",  address: parsedObj.address || ""  });
          return;
        }
      } catch (e) {
        // Strict Check 2: Fallback to comma-separated string mapping (address, city, state, country, pincode)
        const parts = UserGlobalAddress.split(",");
        setUserAddress({address: parts[0]?.trim() || "", city: parts[1]?.trim() || "", state: parts[2]?.trim() || "",country: parts[3]?.trim() || "", pincode: parts[4]?.trim() || "" });
      }
    } else {
      // Global string is empty
      setUserAddress({ country: user?.country_mstr?.countryname || "", state: "", city: "", pincode: "", address: "" });
    }
  }, [UserGlobalAddress]);


   const validate = (postalCountry: countryJsonType | null | undefined) => {
    const newErrors: Record<string, string> = {};
    if (!userAddress.address?.trim())
      newErrors.address = "Address is required";
    if (!userAddress.city?.trim())
      newErrors.city = "City is required";
    if (!userAddress.state?.trim())
      newErrors.state = "State is required";
    if (!userAddress.country?.trim())
      newErrors.country = "Country is required";
    const postalError = validateCountryPostalCode(userAddress.pincode, postalCountry);
    if (postalError) newErrors.pincode = postalError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddressSaveAndClose = async () => {
     let postalCountry = accountCountry;
     try {
       const list = countries.length ? countries : await loadCountryMaster();
       if (!list.length) throw new Error('Country list is empty');
       postalCountry = list.find(country => country.countryid === user.countryid ||
         country.alpha2code === user.country_mstr?.alpha2code) ?? null;
     } catch {
       setErrors(previous => ({ ...previous, pincode: 'Postal code rules are unavailable. Please try again.' }));
       return;
     }
     if (!validate(postalCountry)) return;
    // You can also add User Profile update logic here if you want to save address individually.
    // For now, it flows back to the main profile save.
    updateUserProfile()
    
  };

  const updateUserProfile = async () => {
    setAddressLoading(true)
  
      // SCENARIO LOGIC: Convert local object back to comma separated string.
      // If all are empty, return empty string. Otherwise construct: "address,city,state,country,pincode"
      try {
      const isAddressEmpty = Object.values(userAddress).every((v:any) => v.trim() === "");
      
      const AddressObj  = {address:userAddress.address,city:userAddress?.city,state:userAddress.state,country:userAddress?.country,pincode:userAddress.pincode}

      // const addressStringToSave = isAddressEmpty ? ""  : `${userAddress.address || ""},${userAddress.city || ""},${userAddress.state || ""},${userAddress.country || ""},${userAddress.pincode || ""}`;
      const addressStringToSave = JSON.stringify(AddressObj);
       
      const reqbody = { address: addressStringToSave, // Pushing the strictly formatted string back
      };
  
      const result = await dispatch(UserDetailsAction({ reqBody: reqbody, userid: user.userid }));
      if (result.meta.requestStatus !== "fulfilled") {
        // Keep the address modal open so the user doesn't lose what they typed
        const err: any = result.payload;
        toast.error(err?.response?.data?.message ?? "Failed to save address. Please try again.", { autoClose: 7000 });
        return;
      }
      // The API can answer 200 without storing the address (it echoes the user with address ""), so check what came back
      if ((result.payload as any)?.address !== addressStringToSave) {
        toast.error("Your address could not be saved. Please try again or contact support.", { autoClose: 7000 });
        return;
      }
      // The PUT response is merged into the user; refetch only to pick up anything else the server changed
      await dispatch(getUserDetails(user.userid));
      setErrors({});
      toast.success("Address saved successfully", { autoClose: 7000 });
      handleModalTransition("profile");
      } catch (error) {
        toast.error("Failed to save address. Please try again.", { autoClose: 7000 });
      } finally {
        setAddressLoading(false)
      }
     
    };

  // Updated to manage the local userAddress state instead of Redux projectSetup
    const handleAddressChange = useCallback((props: any, selectedOption?: any) => {
      const target = props?.target ?? props;
      let { name, value } = target;
      
      if (selectedOption) {
        name = selectedOption.name;
        value = props;
      }
      
      // Custom cleanups (length limits and dropdown object mapping)
      if ((name === "address" || name === "city") && value?.length > 100) return;
      if (name === "city" && typeof value === 'object') value = value.label;
      if (name === "state" && typeof value === 'object') value = value.label; 

    if (name === "pincode") {
      value = normalizeCountryPostalInput(String(value), accountCountry);
    }

      setUserAddress((prev:any) => ({  ...prev, [name]: value ?? props!,   ...(name === "state" ? { city: "" } : {}) // Reset city if state changes
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      }, [accountCountry]);

       const closeModal = () => { 
          dispatch(setModalHeaderFooter({title: "", btnTxt: "", secondaryBtnTxt: "", isheader: false,modalid: "" }));
      };
  
  return (
    <>
    {/* 1. Address Modal */}
          {( <NewModal isAbleCLick isBack overflow="scroll" isLoading={addressLoading} onClick={handleAddressSaveAndClose} setIsCLose={() => handleModalTransition("profile")} primarylefticon={<IconSave />}
           name={"Edit Address"} maxheight={"fit-content"} modalSize="md" btnName="profile" onSecondaryClick={() => handleModalTransition("profile")}> 
              <UserAddress error={errors} userAddress={userAddress} handleChange={handleAddressChange} postalMaxLength={postalMaxLength} />
            </NewModal>
          )}
    </>
  )
}

// export default memo(UserAddressModal)
